import API from "api";
import Session from "helpers/session";
import { exportLink } from "helpers/variables";
import {
  buildMetadataHeader,
  buildExportFilename,
  combineMemberExport,
  formatEncountersSection,
  formatNotesSection,
  formatNotificationsSection,
  formatReadingsSection,
} from "views/PatientBoard/microViews/Export/ExportFormatting";

export interface includesInterface {
  type: "csv" | "pdf";
  delivery: "download" | "email";
  emailDestination?: string;
  notifications: boolean;
  notes: boolean;
  encounters: boolean;
  bgl: boolean;
  bp: boolean;
  hrt: boolean;
  spo2: boolean;
  spir: boolean;
  temp: boolean;
  wt: boolean;
  activity: boolean;
  sleep: boolean;
  pain: boolean
  oneCSV: boolean;
}

export const VITALS = ["bp", "bgl", "spo2", "wt", "temp", "spir", "hrt", "activity", "sleep"];

export const handleCombinedCSV = (
  includes: includesInterface,
  start: string,
  end: string,
  token: string,
  memberId: string
) => {
  const readingFetches: { vital: string; promise: Promise<string> }[] = [];

  VITALS.forEach((vital) => {
    if (includes[vital as keyof includesInterface]) {
      readingFetches.push({
        vital,
        promise: API.kore.readings.getForOne(
          memberId,
          token,
          vital,
          `&format=csv&measured_between=${start},${end}`
        ),
      });
    }
  });

  const notesFetch = includes.notes
    ? API.kore.notes.getCSV(
      Session.crackCookieToken("coordinator"),
      `memberId:${memberId}&format=csv&created_between=${start},${end}`
    )
    : Promise.resolve("");

  const notificationsFetch = includes.notifications
    ? API.kore.notifications.getCSV(
      Session.crackCookieToken("coordinator"),
      `memberId:${memberId}&format=csv`
    )
    : Promise.resolve("");

  const encountersFetch = includes.encounters
    ? API.kore.encounters.getCSV(
      Session.crackCookieToken("coordinator"),
      memberId,
      `&format=csv`
    )
    : Promise.resolve("");

  Promise.all([
    Promise.all(readingFetches.map((reading) => reading.promise)),
    notesFetch,
    notificationsFetch,
    encountersFetch,
  ]).then(([readingCSVs, notesCSV, notificationsCSV, encountersCSV]) => {
    const sections: (string | null)[] = [];

    readingFetches.forEach(({ vital }, index) => {
      sections.push(formatReadingsSection(vital, readingCSVs[index]));
    });

    if (includes.encounters) {
      sections.push(formatEncountersSection(encountersCSV));
    }
    if (includes.notes) {
      sections.push(formatNotesSection(notesCSV));
    }
    if (includes.notifications) {
      sections.push(formatNotificationsSection(notificationsCSV));
    }

    const validSections = sections.filter(Boolean);
    if (validSections.length === 0) {
      console.warn(`No data to export for member ${memberId}`);
      return;
    }

    const includesList: string[] = [];
    readingFetches.forEach(({ vital }) => includesList.push(vital));
    if (includes.encounters) includesList.push("encounters");
    if (includes.notes) includesList.push("notes");
    if (includes.notifications) includesList.push("notifications");

    const header = buildMetadataHeader(memberId, start, end, includesList);
    const finalCSV = combineMemberExport(header, sections);
    const filename = buildExportFilename(memberId, start, end);

    exportLink(finalCSV, filename, "text/csv");
  });
};

export const handleMultiCSV = (
  includes: includesInterface,
  start: string,
  end: string,
  token: string,
  memberId: string
) => {
  VITALS.forEach((vital) => {
    if (!includes[vital as keyof includesInterface]) return;

    API.kore.readings
      .getForOne(memberId, token, vital, `&format=csv&measured_between=${start},${end}`)
      .then((rawCSV) => {
        const section = formatReadingsSection(vital, rawCSV);
        if (!section) return;
        const header = buildMetadataHeader(memberId, start, end, [vital]);
        const finalCSV = combineMemberExport(header, [section]);
        const filename = buildExportFilename(memberId, start, end, vital);
        exportLink(finalCSV, filename, "text/csv");
      });
  });

  if (includes.notes) {
    API.kore.notes
      .getCSV(
        Session.crackCookieToken("coordinator"),
        `memberId:${memberId}&format=csv&created_between=${start},${end}`
      )
      .then((rawCSV) => {
        const section = formatNotesSection(rawCSV);
        if (!section) return;
        const header = buildMetadataHeader(memberId, start, end, ["notes"]);
        const finalCSV = combineMemberExport(header, [section]);
        const filename = buildExportFilename(memberId, start, end, "notes");
        exportLink(finalCSV, filename, "text/csv");
      });
  }

  if (includes.notifications) {
    API.kore.notifications
      .getCSV(Session.crackCookieToken("coordinator"), `memberId:${memberId}&format=csv`)
      .then((rawCSV) => {
        const section = formatNotificationsSection(rawCSV);
        if (!section) return;
        const header = buildMetadataHeader(memberId, start, end, ["notifications"]);
        const finalCSV = combineMemberExport(header, [section]);
        const filename = buildExportFilename(memberId, start, end, "notifications");
        exportLink(finalCSV, filename, "text/csv");
      });
  }

  if (includes.encounters) {
    API.kore.encounters
      .getCSV(Session.crackCookieToken("coordinator"), memberId, `&format=csv`)
      .then((rawCSV) => {
        const section = formatEncountersSection(rawCSV);
        if (!section) return;
        const header = buildMetadataHeader(memberId, start, end, ["encounters"]);
        const finalCSV = combineMemberExport(header, [section]);
        const filename = buildExportFilename(memberId, start, end, "encounters");
        exportLink(finalCSV, filename, "text/csv");
      });
  }
};

const handlePDF = (
  includes: includesInterface,
  start: string,
  end: string,
  token: string,
  memberId: string
) => {
  VITALS.forEach((vital) => {
    if (includes[vital as keyof includesInterface]) {
      API.kore.readings.getForOnePDF(
        memberId,
        buildExportFilename(memberId, start, end, vital, "pdf").replace(
          /\.pdf$/,
          ""
        ),
        token,
        vital
      );
    }
  });

  if (includes.notes) {
    API.kore.notes.getExportPDF(
      Session.crackCookieToken("coordinator"),
      buildExportFilename(memberId, start, end, "notes", "pdf").replace(
        /\.pdf$/,
        ""
      ),
      `${memberId}&format=pdf`
    );
  }

  if (includes.encounters) {
    API.kore.encounters.getExportPDF(
      Session.crackCookieToken("coordinator"),
      memberId,
      buildExportFilename(memberId, start, end, "encounters", "pdf").replace(
        /\.pdf$/,
        ""
      ),
      `&format=pdf&updated-between=${start},${end}`
    );
  }

  if (includes.notifications) {
    API.kore.notifications.getExportPDF(
      Session.crackCookieToken("coordinator"),
      buildExportFilename(
        memberId,
        start,
        end,
        "notifications",
        "pdf"
      ).replace(/\.pdf$/, ""),
      `${memberId}&format=pdf`
    );
  }
};

export const submitExport = (
  includes: includesInterface,
  start: number | string | Date | null,
  end: number | string | Date | null,
  token: string,
  memberId: string
) => {
  const startStr = String(start ?? "");
  const endStr = String(end ?? "");

  if (includes.delivery === "email") {
    // Email delivery not yet implemented
    return;
  }

  if (includes.type === "csv") {
    if (includes.oneCSV) {
      handleCombinedCSV(includes, startStr, endStr, token, memberId);
    } else {
      handleMultiCSV(includes, startStr, endStr, token, memberId);
    }
  } else if (includes.type === "pdf") {
    handlePDF(includes, startStr, endStr, token, memberId);
  }
};